import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, Shield, Truck, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/product/ProductCard';
import { CategoryCard } from '../components/product/CategoryCard';
import { PromotionalBanner } from '../components/common/PromotionalBanner';
import { Testimonials } from '../components/common/Testimonials';
import { SEO } from '../components/common/SEO';
import { useProducts, useCategories } from '../hooks/useData';
import { useSiteSettings } from '../hooks/useSiteSettings';

export const Home = () => {
  const { t } = useTranslation();
  const { products } = useProducts({ status: 'active' });
  const { categories } = useCategories();
  const { getSetting } = useSiteSettings();

  const featuredProducts = products.slice(0, 8);
  const featuredCategories = categories.slice(0, 6);

  return (
    <div className="min-h-screen">
      <SEO pageName="home" />
      {/* Hero Section with Full HD Background */}
      <section className="relative min-h-[600px] lg:min-h-[700px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1643379855889-850035817d24?w=1920&q=90"
            alt="Natural beauty"
            className="w-full h-full object-cover"
          />
          {/* Lighter gradient for better image visibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-background/75 via-background/40 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative container-custom py-20 lg:py-32 min-h-[600px] lg:min-h-[700px] flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                {getSetting('hero_title', t('hero.title'))}
                <br />
                <span className="text-primary">{getSetting('hero_title_accent', t('hero.titleAccent'))}</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed max-w-xl">
                {getSetting('hero_subtitle', t('hero.subtitle'))}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/shop">
                  <Button size="lg" className="w-full sm:w-auto shadow-lg">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    {t('hero.shopNow')}
                  </Button>
                </Link>
                <Link to="/categories">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto bg-background/80 backdrop-blur">
                    {t('hero.browseCategories')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Content - Stats Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:flex justify-end"
            >
              <div className="bg-white/90 backdrop-blur p-8 rounded-2xl shadow-xl max-w-xs">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Award className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">200+</p>
                    <p className="text-sm text-muted-foreground">{t('hero.premiumProducts')}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">{t('hero.certifiedQuality')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">{t('hero.freeDelivery')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">{t('hero.trustedWorldwide')}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 border-b bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: 'Certified Quality', description: 'All products tested' },
              { icon: Truck, title: 'Free Delivery', description: 'On orders over €50' },
              { icon: Award, title: 'Premium Brands', description: 'Trusted worldwide' },
              { icon: ShoppingBag, title: 'Secure Shopping', description: '100% safe payment' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 lg:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Shop by Category
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our carefully curated collection of natural cosmetics and pharmaceutical products
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {featuredCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CategoryCard category={category} />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/categories">
              <Button variant="outline" size="lg">
                View All Categories
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Handpicked premium products for your natural beauty routine
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/shop">
              <Button size="lg">
                Shop All Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Promotional Banner */}
      <PromotionalBanner />

      {/* Customer Testimonials */}
      <Testimonials />

      {/* About Section */}
      <section className="py-16 lg:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-elegant">
                <img
                  src={getSetting('about_home_image', 'https://images.unsplash.com/photo-1560879311-370fd4561a0d?w=1200&q=85')}
                  alt="Premium cosmetic products"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-6">
                {getSetting('about_home_title', 'Your Trusted Partner in Natural Wellness')}
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {getSetting('about_home_paragraph_1', 'At NaturalCare, we believe in the power of nature. Every product in our collection is carefully selected to meet the highest standards of quality, safety, and effectiveness.')}
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {getSetting('about_home_paragraph_2', "From premium cosmetics to pharmaceutical-grade wellness products, we're committed to helping you achieve natural beauty and optimal health.")}
              </p>
              <Link to="/about">
                <Button size="lg">
                  {getSetting('about_home_button_text', 'Learn More About Us')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};